<template>
  <div class="task-panels-container">
    <!-- Toast 提示 -->
    <transition name="toast-fade">
      <div v-if="toast.show" :class="['toast', 'toast-' + toast.type]" @click="toast.show = false">
        {{ toast.message }}
      </div>
    </transition>

    <!-- ==================== 任务管理工具栏 ==================== -->
    <div class="task-toolbar">
      <h3>任务管理</h3>
      <div class="task-selector">
        <label>当前任务：</label>
        <select :value="selectedTaskId" @change="$emit('task-change', $event.target.value)" :disabled="isLoading || tasks.length === 0" :class="{ 'select-empty': tasks.length === 0 }">
          <option v-if="tasks.length === 0" value="" disabled>无任务</option>
          <option v-for="task in tasks" :key="task.id" :value="task.id">
            {{ formatCrawlerTaskLabel(task) }} ({{ task.report_period }}) - {{ getTaskStatusText(task.status) }}
          </option>
        </select>
        <span v-if="selectedTask" class="task-stats">
          共 {{ selectedTask.result_count || 0 }} 条 | 
          有效 {{ selectedTask.valid_count || 0 }} 条
        </span>
      </div>
      <div class="task-actions">
        <button class="btn btn-primary" @click="showCreateDialog = true">
          + 创建新任务
        </button>
        <button class="btn btn-secondary" @click="openTaskList" v-if="tasks.length > 0">
          管理任务
        </button>
      </div>
    </div>

    <!-- ==================== Step 1 · 链接爬取 ==================== -->
    <div v-if="selectedTask" class="task-panel crawler-panel">
      <div class="step-header">
        <span class="step-number">1</span>
        <h3>链接爬取</h3>
      </div>
      <div class="crawl-side-hint">
        <span class="hint-icon">ⓘ</span>
        建议先到侧栏 <strong>财报数据 → 公司信息</strong> 维护参考表，便于「数据同步」页展示公司完整信息；也可先爬取数据，后补传公司信息。
      </div>
    
      <!-- 任务进度显示 - 爬取链接阶段 -->
      <div v-if="selectedTask.status === 'crawling'" class="task-progress crawling-progress">
        <div class="progress-header">
          <span class="status-badge crawling">爬取链接中...</span>
          <span class="auto-refresh">自动刷新中</span>
        </div>
        <div class="progress-step">
          <div class="progress-label">
            <span>{{ crawlProgress > 0 ? '查询交易所 & 验证PDF链接' : '正在启动爬虫脚本...' }}</span>
            <span v-if="crawlProgress > 0" class="progress-numbers">
              {{ crawlProgress }}%
            </span>
          </div>
          <div class="progress-bar crawl-bar">
            <div 
              class="progress-fill crawling animated" 
              :style="{ width: (crawlProgress > 0 ? crawlProgress : 2) + '%' }"
            ></div>
          </div>
        </div>
        <button class="btn btn-danger btn-sm" @click="cancelTask">中断任务</button>
      </div>
    
      <!-- 链接爬取阶段中断 -->
      <div v-else-if="crawlPhaseInterrupted" class="task-failed-section">
        <div class="failed-info">
          <h4>链接爬取已{{ selectedTask.status === 'cancelled' ? '取消' : '中断' }}</h4>
          <p>当前进度：<strong>{{ selectedTask.processed_companies || 0 }} / {{ selectedTask.total_companies || 0 }}</strong></p>
          <p v-if="selectedTask.result_count > 0">已收集 <strong>{{ selectedTask.result_count }}</strong> 条链接</p>
          <p class="hint">将自动跳过已写入的数据（断点续传）</p>
        </div>
        <button class="btn btn-warning" @click="continueTask(selectedTask)">
          继续爬取链接
        </button>
      </div>
    
      <!-- 链接爬取已完成状态 -->
      <div v-else-if="crawlPhaseCompleted && selectedTask.result_count > 0" class="crawl-completed-section">
        <div class="completed-info">
          <span class="status-icon completed-icon"></span>
          <span class="status-text">链接爬取已完成</span>
          <span class="result-stats">
            共 <strong>{{ selectedTask.result_count || 0 }}</strong> 家公司 | 
            有效 <strong>{{ selectedTask.valid_count || 0 }}</strong> 条
          </span>
        </div>
        <p class="link-crawl-desc">以下为前 5 条预览（证券代码、公司名称、财报名称、报告日期、PDF 链接）。</p>
        <div v-if="linkCrawlPreviewLoading" class="step-idle-hint">加载预览中…</div>
        <div v-else-if="linkCrawlPreviewRows.length" class="mini-table-wrap">
          <table class="mini-table link-crawl-table">
            <thead>
              <tr>
                <th>证券代码</th>
                <th>公司名称</th>
                <th>财报名称</th>
                <th>报告日期</th>
                <th>PDF链接</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(row, idx) in linkCrawlPreviewRows" :key="row.id || idx">
                <td>{{ row.sec_code || '—' }}</td>
                <td>{{ row.company_name || '—' }}</td>
                <td>{{ row.report_name || '—' }}</td>
                <td>{{ formatReportDate(row.report_date) }}</td>
                <td class="pdf-link-cell">
                  <a v-if="row.pdf_url" :href="row.pdf_url" target="_blank" rel="noopener">查看</a>
                  <span v-else>—</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- 尚未开始 -->
      <div v-else class="step-idle-hint">
        <span>创建任务后点击"开始"启动链接爬取</span>
      </div>

      <div v-if="canExportLinkCrawl" class="link-export-footer">
        <button
          type="button"
          class="btn btn-link-export"
          :disabled="isLinkExportLoading"
          @click="downloadLinkCrawlExcel"
        >
          {{ isLinkExportLoading ? '导出中...' : '下载 Excel（链接爬取数据）' }}
        </button>
      </div>
    </div>

    <!-- 步骤连接线 -->
    <div v-if="selectedTask" class="step-connector">
      <span class="connector-arrow">↓</span>
    </div>

    <!-- ==================== Step 2 · 公司信息（仅预览与跳转） ==================== -->
    <div v-if="selectedTask" class="task-panel company-info-step">
      <div class="step-header">
        <span class="step-number">2</span>
        <h3>公司信息</h3>
      </div>
      <p class="company-step-desc">
        以下为表内前 5 条预览，完整列表、列映射上传与导出请前往「公司信息」Tab。
      </p>
      <p v-if="selectedTask.result_count" class="ref-compare subtle">
        当前任务已收集 <strong>{{ selectedTask.result_count }}</strong> 条链接（仅供参考对比，非阻断）。
      </p>
      <div v-if="listedPreviewLoading" class="step-idle-hint">加载预览中…</div>
      <div v-else-if="listedPreviewRows.length" class="mini-table-wrap">
        <table class="mini-table">
          <thead>
            <tr>
              <th>证券代码</th>
              <th>简称</th>
              <th>省份</th>
              <th>行业</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(r, idx) in listedPreviewRows" :key="r.id || idx">
              <td>{{ r.sec_code }}</td>
              <td>{{ r.sec_name || '—' }}</td>
              <td>{{ r.province || '—' }}</td>
              <td>{{ r.industry || '—' }}</td>
            </tr>
          </tbody>
        </table>
      </div>
      <div v-else class="step-idle-hint">表中暂无数据；可先爬取与解析，再在「公司信息」上传，数据同步页刷新即可 JOIN。</div>
      <button type="button" class="btn btn-primary btn-sm go-company-btn" @click="goCompanyInfo">去公司信息维护 / 上传</button>
    </div>

    <div v-if="selectedTask" class="step-connector">
      <span class="connector-arrow">↓</span>
    </div>

    <!-- ==================== Step 3 · PDF解析 ==================== -->
    <div v-if="selectedTask" class="task-panel pdf-panel" :class="{ 'panel-disabled': !selectedTask.result_count }">
      <div class="step-header">
        <span class="step-number">3</span>
        <h3>PDF解析</h3>
        <span :class="'parse-status-badge status-' + getPdfParseStatus()">
          {{ getPdfParseStatusText() }}
        </span>
      </div>
      
      <template v-if="selectedTask.result_count > 0">
        <!-- 正在解析时显示动态状态 -->
        <div v-if="isPdfParseActive" class="parsing-live-status">
          <div class="live-indicator">
            <span class="live-dot"></span>
            <span>{{ selectedTask.status === 'parse_queued' ? '排队等待解析槽位...' : '正在解析中...' }}</span>
            <span
              v-if="selectedTask.parse_pdf_pages_total"
              class="pdf-page-progress"
            > · PDF {{ selectedTask.parse_pdf_pages_done || 0 }}/{{ selectedTask.parse_pdf_pages_total }} 页</span>
            <span class="auto-refresh-badge">自动刷新</span>
          </div>
        </div>
        
        <!-- 解析进度统计 -->
        <div class="parse-stats-grid">
          <div class="stat-item">
            <span class="stat-label">总链接数</span>
            <span class="stat-value">{{ selectedTask.result_count || 0 }}</span>
          </div>
          <div class="stat-item success">
            <span class="stat-label">已完成</span>
            <span class="stat-value">{{ selectedTask.parsed_count || 0 }}</span>
          </div>
          <div class="stat-item pending">
            <span class="stat-label">待处理</span>
            <span class="stat-value">{{ realPendingCount }}</span>
          </div>
          <div class="stat-item failed" v-if="(selectedTask.failed_count || 0) > 0">
            <span class="stat-label">失败</span>
            <span class="stat-value">{{ selectedTask.failed_count || 0 }}</span>
          </div>
        </div>
        
        <!-- 进度条 -->
        <div class="parse-progress-section">
          <div class="progress-bar large">
            <div 
              class="progress-fill" 
              :class="isPdfParseActive ? 'parsing animated' : 'completed'"
              :style="{ width: parseProgress + '%' }"
            ></div>
          </div>
          <span class="progress-text">{{ parseProgress }}% 完成</span>
        </div>
        
        <!-- 操作按钮区 -->
        <div class="parse-actions">
          <button 
            v-if="isPdfParseActive"
            class="btn btn-danger" 
            @click="cancelTask"
          >
            中断解析
          </button>
          <button 
            v-if="!isPdfParseActive && hasUnparsedRecords"
            class="btn btn-primary" 
            @click="wasParsing ? continueParsing() : showParseDialog = true"
          >
            {{ wasParsing ? '继续解析PDF' : '开始解析PDF' }}
          </button>
          <button 
            v-if="!isPdfParseActive && (selectedTask.failed_count || 0) > 0" 
            class="btn btn-warning" 
            @click="retryFailedRecords"
          >
            重试失败记录 ({{ selectedTask.failed_count }})
          </button>
          <span 
            v-if="!isPdfParseActive && !hasUnparsedRecords && (selectedTask.failed_count || 0) === 0 && (selectedTask.parsed_count || 0) > 0"
            class="all-done-text"
          >
            所有PDF已解析完成！
          </span>
        </div>
        
        <!-- 错误/状态信息显示 -->
        <div v-if="selectedTask.error_message && ['failed', 'cancelled'].includes(selectedTask.status)" class="error-info">
          <span class="error-icon">!</span>
          <span class="error-text">{{ selectedTask.error_message }}</span>
        </div>
      </template>
      <div v-else class="step-idle-hint">
        <span>等待链接爬取完成后可开始解析</span>
      </div>
    </div>
    <!-- ==================== PDF解析任务框结束 ==================== -->

    <!-- 创建任务对话框 -->
    <transition name="modal-fade">
    <div class="modal" v-if="showCreateDialog" @click.self="showCreateDialog = false">
      <div class="modal-content">
        <h3>创建爬取任务</h3>
        <div class="form-group">
          <label>报告类型：</label>
          <div class="radio-group">
            <label v-for="(name, code) in reportTypes" :key="code">
              <input type="radio" v-model="newTask.reportType" :value="code" @change="onReportTypeChange" />
              {{ name }}
            </label>
          </div>
        </div>
        <div class="form-group">
          <label>年份：</label>
          <select v-model.number="newTask.year" @change="onReportTypeChange" class="year-select">
            <option v-for="y in yearOptions" :key="y" :value="y">{{ y }} 年</option>
          </select>
        </div>
        <div class="form-group crawl-date-group" v-if="computedDateRange.start">
          <label>爬取日期</label>
          <div class="crawl-date-row">
            <input
              type="date"
              v-model="crawlDateStart"
              class="crawl-date-input"
              :max="crawlDateEnd || undefined"
            />
            <span class="crawl-date-sep">至</span>
            <input
              type="date"
              v-model="crawlDateEnd"
              class="crawl-date-input"
              :min="crawlDateStart || undefined"
            />
          </div>
        </div>
        <div class="modal-actions">
          <button class="btn btn-secondary" @click="showCreateDialog = false">取消</button>
          <button class="btn btn-primary" @click="createTask" :disabled="isCreating">
            {{ isCreating ? '创建中...' : '开始爬取' }}
          </button>
        </div>
      </div>
    </div>
    </transition>

    <!-- 开始解析对话框 -->
    <transition name="modal-fade">
    <div class="modal" v-if="showParseDialog" @click.self="showParseDialog = false">
      <div class="modal-content">
        <h3>开始解析PDF</h3>
        <div class="form-group">
          <label>解析范围：</label>
          <div class="range-input">
            从第 <input type="number" v-model.number="parseConfig.rowStart" min="1" style="width:80px" /> 行
            到第 <input type="number" v-model.number="parseConfig.rowEnd" :min="parseConfig.rowStart" style="width:80px" placeholder="全部" /> 行
          </div>
          <span class="hint">（留空"到第X行"表示解析到最后一行）</span>
        </div>
        <div class="estimate-info" v-if="selectedTask">
          <p><strong>预估信息</strong></p>
          <p>• 总共：{{ selectedTask.result_count || 0 }} 家公司</p>
          <p>• 本次处理：{{ calculateParseCount() }} 家公司</p>
          <p>• 预估耗时：约 {{ estimateParseTime() }}</p>
        </div>
        <div class="modal-actions">
          <button class="btn btn-secondary" @click="showParseDialog = false">取消</button>
          <button class="btn btn-primary" @click="startParsing" :disabled="isParsing">
            {{ isParsing ? '启动中...' : '开始解析' }}
          </button>
        </div>
      </div>
    </div>
    </transition>

    <!-- 任务管理对话框 -->
    <transition name="modal-fade">
    <div class="modal" v-if="showTaskList" @click.self="showTaskList = false">
      <div class="modal-content modal-large">
        <h3>任务管理</h3>
        <p class="stats-info">共 {{ tasks.length }} 个任务</p>
        <table class="task-table">
          <thead>
            <tr>
              <th class="th-checkbox">
                <input type="checkbox" :checked="tasksToDelete.length === tasks.length && tasks.length > 0" @change="toggleSelectAll" />
              </th>
              <th>任务名称</th>
              <th>报告时间</th>
              <th>状态</th>
              <th>记录数</th>
              <th>同步</th>
              <th>创建时间</th>
              <th>操作</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="task in tasks" :key="task.id" :class="{ 'row-selected': tasksToDelete.includes(task.id) }">
              <td class="td-checkbox">
                <input type="checkbox" v-model="tasksToDelete" :value="task.id" />
              </td>
              <td>{{ formatCrawlerTaskLabel(task) }}</td>
              <td>{{ task.report_period }}</td>
              <td>
                <span :class="'status-' + task.status">{{ getTaskStatusText(task.status) }}</span>
              </td>
              <td>{{ task.result_count || 0 }} (有效: {{ task.valid_count || 0 }})</td>
              <td>
                <span :class="'sync-status sync-' + (parseInt(task.synced_count || 0) > 0 ? 'yes' : 'no')">
                  {{ parseInt(task.synced_count || 0) > 0 ? '已同步' : '未同步' }}
                </span>
              </td>
              <td>{{ formatDate(task.created_at) }}</td>
              <td>
                <button class="btn btn-xs" @click="selectTask(task.id)">查看</button>
                <button 
                  v-if="(task.status === 'failed' || task.status === 'cancelled') && !task.result_count"
                  class="btn btn-xs btn-warning" 
                  @click="continueTask(task)"
                >
                  继续爬取
                </button>
              </td>
            </tr>
          </tbody>
        </table>
        <p class="warning" v-if="tasksToDelete.length > 0">
          已选 {{ tasksToDelete.length }} 个任务，删除不可恢复
        </p>
        <div class="modal-actions">
          <button class="btn btn-danger" v-if="tasksToDelete.length > 0" @click="deleteSelectedTasks">
            删除选中 ({{ tasksToDelete.length }})
          </button>
          <button class="btn btn-secondary" @click="showTaskList = false">关闭</button>
        </div>
      </div>
    </div>
    </transition>
  </div>
</template>

<script>
import { createApiClient } from '@/services/httpSetup';
import { toastMixin } from '@/utils/toastMixin.js';
import { formatCrawlerTaskLabel } from '@/utils/crawlerTaskLabel.js';

const apiClient = createApiClient({
  baseURL: '/api/crawler'
});

export default {
  name: 'CrawlerTaskPanel',
  mixins: [toastMixin],
  props: {
    tasks: { type: Array, default: () => [] },
    selectedTaskId: { type: [String, Number], default: '' },
    selectedTask: { type: Object, default: null },
    isLoading: { type: Boolean, default: false }
  },
  emits: ['task-change', 'task-created', 'task-deleted', 'refresh', 'select-task', 'switch-crawler-tab'],
  data() {
    return {
      listedPreviewRows: [],
      listedPreviewTotal: 0,
      listedPreviewLoading: false,
      linkCrawlPreviewRows: [],
      linkCrawlPreviewLoading: false,
      isLinkExportLoading: false,
      showCreateDialog: false,
      showParseDialog: false,
      showTaskList: false,
      newTask: {
        reportType: 'sjdbg',
        year: new Date().getFullYear()
      },
      crawlDateStart: '',
      crawlDateEnd: '',
      parseConfig: {
        rowStart: 1,
        rowEnd: null
      },
      isCreating: false,
      isParsing: false,
      tasksToDelete: [],
      reportTypes: {
        'yjdbg': '一季度报',
        'bndbg': '半年报',
        'sjdbg': '三季度报',
        'ndbg': '年报'
      }
    };
  },
  computed: {
    yearOptions() {
      const current = new Date().getFullYear();
      return [current, current - 1, current - 2];
    },
    computedDateRange() {
      const dateRangeMap = {
        'yjdbg': (y) => ({ start: `${y}-04-01`, end: `${y}-06-30`, reportPeriod: `${y}Q1 一季度报` }),
        'bndbg': (y) => ({ start: `${y}-07-01`, end: `${y}-09-30`, reportPeriod: `${y}Q2 半年报` }),
        'sjdbg': (y) => ({ start: `${y}-10-01`, end: `${y}-12-31`, reportPeriod: `${y}Q3 三季度报` }),
        'ndbg':  (y) => ({ start: `${y + 1}-01-01`, end: `${y + 1}-04-30`, reportPeriod: `${y}Q4 年报` })
      };
      const fn = dateRangeMap[this.newTask.reportType];
      if (!fn) return { start: '', end: '', reportPeriod: '' };
      return fn(this.newTask.year);
    },
    // 爬取阶段是否已完成（link_collected_at 有值，或状态已进入后续阶段）
    crawlPhaseCompleted() {
      if (!this.selectedTask) return false;
      return !!this.selectedTask.link_collected_at ||
        ['link_collected', 'parsing', 'parse_queued', 'completed'].includes(this.selectedTask.status);
    },
    // 爬取阶段是否被中断（cancelled 一定是爬取中断；failed 且没走到 link_collected 也是爬取中断）
    crawlPhaseInterrupted() {
      if (!this.selectedTask) return false;
      return this.selectedTask.status === 'cancelled' ||
        (this.selectedTask.status === 'failed' && !this.selectedTask.link_collected_at);
    },
    crawlProgress() {
      if (!this.selectedTask) return 0;
      return this.selectedTask.progress || 0;
    },
    parseProgress() {
      if (!this.selectedTask) return 0;
      const parsed = this.selectedTask.parsed_count || 0;
      const total = this.selectedTask.result_count || 1;
      return Math.round((parsed / total) * 100);
    },
    isPdfParseActive() {
      if (!this.selectedTask) return false;
      return this.selectedTask.status === 'parsing' || this.selectedTask.status === 'parse_queued';
    },
    // 判断是否还有未解析的记录
    hasUnparsedRecords() {
      if (!this.selectedTask) return false;
      const parsed = this.selectedTask.parsed_count || 0;
      const total = this.selectedTask.result_count || 0;
      const pending = this.selectedTask.pending_count || 0;
      // 如果 pending > 0，或者 parsed < total（说明还有记录没解析完）
      return pending > 0 || (total > 0 && parsed < total);
    },
    // 真正的待处理数量（总数 - 已完成 - 失败）
    realPendingCount() {
      if (!this.selectedTask) return 0;
      const total = this.selectedTask.result_count || 0;
      const parsed = this.selectedTask.parsed_count || 0;
      const failed = this.selectedTask.failed_count || 0;
      return Math.max(0, total - parsed - failed);
    },
    // 判断任务是否已经开始过解析（用于决定按钮文字"继续"还是"开始"）
    wasParsing() {
      if (!this.selectedTask) return false;
      const parsed = this.selectedTask.parsed_count || 0;
      const failed = this.selectedTask.failed_count || 0;
      if (parsed > 0 || failed > 0) return true;
      const msg = this.selectedTask.error_message || '';
      return msg.includes('中断') || msg.includes('继续');
    },
    canExportLinkCrawl() {
      if (!this.selectedTask?.result_count) return false;
      return this.crawlPhaseCompleted || this.crawlPhaseInterrupted;
    }
  },
  watch: {
    selectedTask: {
      handler() {
        this.loadListedPreview();
        this.loadLinkCrawlPreview();
      },
      immediate: true
    },
    showCreateDialog(open) {
      if (open) this.syncCrawlDatesFromDefaults();
    },
    'newTask.reportType'() {
      if (this.showCreateDialog) this.syncCrawlDatesFromDefaults();
    },
    'newTask.year'() {
      if (this.showCreateDialog) this.syncCrawlDatesFromDefaults();
    }
  },
  methods: {
    formatCrawlerTaskLabel,

    formatReportDate(value) {
      if (value == null || value === '') return '—';
      const text = String(value);
      if (/^\d{4}-\d{2}-\d{2}/.test(text)) return text.slice(0, 10);
      return text;
    },

    async loadLinkCrawlPreview() {
      if (!this.selectedTaskId || !this.selectedTask?.result_count) {
        this.linkCrawlPreviewRows = [];
        return;
      }
      this.linkCrawlPreviewLoading = true;
      try {
        const { data } = await apiClient.get('/results', {
          params: {
            taskId: this.selectedTaskId,
            page: 1,
            pageSize: 5
          }
        });
        this.linkCrawlPreviewRows = data.data || [];
      } catch (e) {
        this.linkCrawlPreviewRows = [];
      } finally {
        this.linkCrawlPreviewLoading = false;
      }
    },

    downloadLinkCrawlExcel() {
      if (!this.selectedTaskId) return;
      this.isLinkExportLoading = true;
      const url = `/api/crawler/tasks/${this.selectedTaskId}/link-export`;
      window.open(url, '_blank');
      setTimeout(() => {
        this.isLinkExportLoading = false;
      }, 800);
    },

    async loadListedPreview() {
      this.listedPreviewLoading = true;
      try {
        const { data } = await apiClient.get('/listed-information/preview', { params: { limit: 5 } });
        this.listedPreviewRows = data.rows || [];
        this.listedPreviewTotal = data.total != null ? data.total : 0;
      } catch (e) {
        this.listedPreviewRows = [];
        this.listedPreviewTotal = 0;
      } finally {
        this.listedPreviewLoading = false;
      }
    },
    goCompanyInfo() {
      this.$emit('switch-crawler-tab', 'crawler-company-info');
    },
    getTaskStatusText(status) {
      const statusMap = {
        'pending': '等待中',
        'crawling': '爬取链接中',
        'link_collected': '链接已收集',
        'parsing': '解析PDF中',
        'parse_queued': '解析排队中',
        'completed': '已完成',
        'failed': '失败',
        'cancelled': '已取消'
      };
      return statusMap[status] || status;
    },
    
    // 获取PDF解析状态（须与任务 status 一致：解析中不可显示「已中断」）
    getPdfParseStatus() {
      if (!this.selectedTask) return 'pending';

      const parsed = parseInt(this.selectedTask.parsed_count, 10) || 0;
      const pending = parseInt(this.selectedTask.pending_count, 10) || 0;
      const failed = parseInt(this.selectedTask.failed_count, 10) || 0;
      const total = parseInt(this.selectedTask.result_count, 10) || 0;

      if (this.selectedTask.status === 'parsing' || this.selectedTask.status === 'parse_queued') return 'parsing';

      if (parsed === total && total > 0 && failed === 0) return 'completed';
      if (failed > 0) return 'failed';
      if (this.wasParsing && pending > 0) return 'interrupted';
      if (parsed > 0 && pending > 0) return 'partial';
      if (pending === total) return 'pending';
      return 'pending';
    },
    
    // 获取PDF解析状态文字
    getPdfParseStatusText() {
      const status = this.getPdfParseStatus();
      const statusMap = {
        'parsing': '解析中',
        'completed': '已完成',
        'failed': '有失败',
        'interrupted': '已中断',
        'partial': '部分完成',
        'pending': '待解析'
      };
      return statusMap[status] || '未知';
    },
    
    formatDate(dateStr) {
      if (!dateStr) return '-';
      return new Date(dateStr).toLocaleString('zh-CN');
    },
    
    onReportTypeChange() {
      // 日期范围由 computedDateRange 自动计算，无需请求后端
    },

    syncCrawlDatesFromDefaults() {
      const r = this.computedDateRange;
      if (r.start && r.end) {
        this.crawlDateStart = r.start;
        this.crawlDateEnd = r.end;
      }
    },
    
    async createTask() {
      if (!this.newTask.reportType || !this.newTask.year) {
        this.showToast('请填写完整的任务信息', 'warning');
        return;
      }
      if (!this.crawlDateStart || !this.crawlDateEnd) {
        this.showToast('请选择爬取起止日期', 'warning');
        return;
      }
      if (this.crawlDateStart > this.crawlDateEnd) {
        this.showToast('开始日期不能晚于结束日期', 'warning');
        return;
      }
      
      this.isCreating = true;
      try {
        const res = await apiClient.post('/tasks', {
          reportType: this.newTask.reportType,
          year: this.newTask.year,
          startDate: this.crawlDateStart,
          endDate: this.crawlDateEnd
        });
        
        this.showToast('任务创建成功！', 'success');
        this.showCreateDialog = false;
        this.$emit('task-created', res.data.task);
      } catch (err) {
        this.showToast('创建任务失败: ' + (err.response?.data?.error || err.message), 'error');
      } finally {
        this.isCreating = false;
      }
    },
    
    calculateParseCount() {
      if (!this.selectedTask) return 0;
      const total = this.selectedTask.result_count || 0;
      const start = this.parseConfig.rowStart || 1;
      const end = this.parseConfig.rowEnd || total;
      return Math.max(0, Math.min(end, total) - start + 1);
    },
    
    estimateParseTime() {
      const count = this.calculateParseCount();
      if (count === 0) return '0分钟';
      const seconds = count * 3;
      const minutes = Math.ceil(seconds / 60);
      if (minutes < 60) return `${minutes}分钟`;
      return `${Math.floor(minutes / 60)}小时${minutes % 60}分钟`;
    },
    
    async continueParsing() {
      if (!this.selectedTask) return;
      this.isParsing = true;
      try {
        const res = await apiClient.post(`/tasks/${this.selectedTask.id}/start-parsing`);
        this.showToast(res.data?.message || '已提交解析', res.data?.queued ? 'info' : 'success');
        this.$emit('refresh');
      } catch (err) {
        this.showToast('继续解析失败: ' + (err.response?.data?.error || err.message), 'error');
      } finally {
        this.isParsing = false;
      }
    },

    async startParsing() {
      if (!this.selectedTask) return;
      
      const total = this.selectedTask.result_count || 0;
      const rowStart = this.parseConfig.rowStart || 1;
      const rowEnd = this.parseConfig.rowEnd || total;
      
      if (rowStart < 1 || rowStart > total) {
        this.showToast(`起始行数必须在 1-${total} 之间`, 'warning');
        return;
      }
      
      this.isParsing = true;
      try {
        const res = await apiClient.post(`/tasks/${this.selectedTask.id}/start-parsing`, {
          parseRowStart: rowStart,
          parseRowEnd: rowEnd
        });
        
        this.showToast(res.data?.message || 'PDF解析任务已启动！', res.data?.queued ? 'info' : 'success');
        this.showParseDialog = false;
        this.parseConfig = { rowStart: 1, rowEnd: null };
        this.$emit('refresh');
      } catch (err) {
        this.showToast('启动解析失败: ' + (err.response?.data?.error || err.message), 'error');
      } finally {
        this.isParsing = false;
      }
    },
    
    async cancelTask() {
      if (!confirm('确定要中断当前任务吗？')) return;
      try {
        await apiClient.post(`/tasks/${this.selectedTaskId}/cancel`);
        this.$emit('refresh');
      } catch (err) {
        this.showToast('中断任务失败: ' + (err.response?.data?.error || err.message), 'error');
      }
    },
    
    async continueTask(task) {
      if (!confirm(`继续爬取链接"${task.task_name}"？\n将自动跳过已写入的数据。`)) return;
      try {
        await apiClient.post(`/tasks/${task.id}/continue`);
        this.showToast('链接爬取已重新启动！', 'success');
        this.showTaskList = false;
        this.$emit('select-task', task.id);
        this.$emit('refresh');
      } catch (err) {
        this.showToast('继续任务失败: ' + (err.response?.data?.error || err.message), 'error');
      }
    },
    
    async retryFailedRecords() {
      const failedCount = this.selectedTask.failed_count || 0;
      if (!confirm(`确定要将 ${failedCount} 条失败记录重置为待处理状态吗？\n重置后可点击"继续解析PDF"来重新处理这些记录。`)) return;
      
      try {
        const response = await apiClient.post(`/tasks/${this.selectedTaskId}/retry-failed`);
        this.showToast(response.data.message || '失败记录已重置！', 'success');
        this.$emit('refresh');
      } catch (err) {
        this.showToast('重置失败记录失败: ' + (err.response?.data?.error || err.message), 'error');
      }
    },
    
    openTaskList() {
      this.tasksToDelete = [];
      this.showTaskList = true;
    },
    
    toggleSelectAll(e) {
      this.tasksToDelete = e.target.checked ? this.tasks.map(t => t.id) : [];
    },
    
    selectTask(taskId) {
      this.$emit('select-task', taskId);
      this.showTaskList = false;
    },
    
    async deleteSelectedTasks() {
      if (this.tasksToDelete.length === 0) return;
      if (!confirm(`确定要删除选中的 ${this.tasksToDelete.length} 个任务吗？\n删除不可恢复。`)) return;
      
      try {
        await apiClient.post('/tasks/batch-delete', { taskIds: this.tasksToDelete });
        this.showToast('删除成功', 'success');
        this.tasksToDelete = [];
        this.$emit('refresh');
      } catch (err) {
        this.showToast('删除失败: ' + (err.response?.data?.error || err.message), 'error');
      }
    }
  },
  mounted() {
    // 日期范围由 computedDateRange 自动计算
  }
};
</script>

<style scoped>
/* 容器样式 */
.task-panels-container {
  display: flex;
  flex-direction: column;
  gap: 0;
}

/* ========== 任务管理工具栏 ========== */
.task-toolbar {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  padding: 12px 0 16px;
  margin-bottom: 20px;
  border-bottom: 2px solid #e0e0e0;
}

.task-toolbar h3 {
  margin: 0;
  color: #2e3968;
  white-space: nowrap;
}

/* ========== 步骤卡片通用 ========== */
.task-panel {
  background: #fff;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
}

.step-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
}

.link-export-footer {
  display: flex;
  justify-content: flex-end;
  margin-top: 16px;
}

.btn-link-export {
  background: transparent;
  color: #2e3968;
  border: 1px solid #2e3968;
  padding: 8px 20px;
  border-radius: 4px;
  font-size: 14px;
  cursor: pointer;
  white-space: nowrap;
  transition: color 0.2s ease, border-color 0.2s ease, background-color 0.2s ease;
}

.btn-link-export:hover:not(:disabled) {
  background: rgba(46, 57, 104, 0.06);
  color: #2e3968;
  border-color: #2e3968;
}

.btn-link-export:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.step-header h3 {
  margin: 0;
  color: #2e3968;
  font-size: 16px;
}

.step-number {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: #2e3968;
  color: #fff;
  font-size: 14px;
  font-weight: 700;
  flex-shrink: 0;
}

.crawl-side-hint {
  font-size: 13px;
  color: #5c6b8a;
  background: #f0f4fc;
  border: 1px solid #d9e2f3;
  border-radius: 6px;
  padding: 10px 12px;
  margin-bottom: 14px;
  line-height: 1.5;
}
.crawl-side-hint .hint-icon {
  margin-right: 6px;
}
.company-info-step .company-step-desc {
  margin: 0 0 8px;
  font-size: 14px;
  color: #444;
  line-height: 1.5;
}
.company-info-step .subtle,
.ref-compare.subtle {
  font-size: 13px;
  color: #888;
  margin: 0 0 12px;
}
.mini-table-wrap {
  overflow-x: auto;
  margin-bottom: 12px;
}
.mini-table {
  border-collapse: collapse;
  font-size: 13px;
  width: 100%;
  max-width: 640px;
}
.mini-table th,
.mini-table td {
  border: 1px solid #e0e0e0;
  padding: 6px 10px;
  text-align: left;
}
.mini-table th {
  background: #f5f7fa;
  color: #2e3968;
}
.go-company-btn {
  margin-top: 4px;
}

.step-idle-hint {
  color: #999;
  font-size: 14px;
  padding: 8px 0;
}

/* ========== 步骤连接线 ========== */
.step-connector {
  display: flex;
  justify-content: center;
  padding: 6px 0;
}

.connector-arrow {
  color: #bbb;
  font-size: 18px;
  line-height: 1;
}

/* ========== 爬虫任务面板 ========== */
.crawler-panel {
  border-left: 4px solid #2e3968;
}

/* ========== PDF解析任务面板 ========== */
.pdf-panel {
  border-left: 4px solid #667eea;
  background: linear-gradient(135deg, #f8f9ff 0%, #fff 100%);
}

.pdf-panel .step-header {
  border-bottom: 1px solid #e0e0e0;
  padding-bottom: 12px;
}

.pdf-panel .step-number {
  background: #667eea;
}

/* 禁用态 */
.panel-disabled {
  opacity: 0.5;
  pointer-events: none;
}

.panel-disabled .step-number {
  background: #ccc;
}

/* 实时解析状态指示器 */
.parsing-live-status {
  margin-bottom: 16px;
}

.live-indicator {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 16px;
  background: linear-gradient(135deg, #e8f5e9 0%, #f1f8e9 100%);
  border-radius: 8px;
  border: 1px solid #a5d6a7;
}

.live-dot {
  width: 10px;
  height: 10px;
  background: #4caf50;
  border-radius: 50%;
  animation: pulse-dot 1.5s infinite;
}

@keyframes pulse-dot {
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.5; transform: scale(1.2); }
}

.auto-refresh-badge {
  margin-left: auto;
  font-size: 12px;
  color: #666;
  animation: pulse 2s infinite;
}

.link-crawl-desc {
  margin: 12px 0 8px;
  font-size: 13px;
  color: #606266;
}

.link-crawl-table {
  max-width: 100%;
}

.pdf-link-cell a {
  color: #2e3968;
  text-decoration: none;
}

.pdf-link-cell a:hover {
  text-decoration: underline;
}

/* 链接爬取完成状态 */
.crawl-completed-section {
  margin-top: 16px;
  padding: 12px 16px;
  background: linear-gradient(135deg, #e8f5e9 0%, #f1f8e9 100%);
  border-radius: 8px;
  border: 1px solid #a5d6a7;
}

.completed-info {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.completed-info .status-icon {
  font-size: 20px;
}

.completed-info .status-text {
  font-weight: 600;
  color: #2e7d32;
}

.completed-info .result-stats {
  margin-left: auto;
  color: #555;
  font-size: 14px;
}

.task-selector {
  display: flex;
  align-items: center;
  gap: 8px;
}

.task-selector select {
  padding: 6px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  min-width: 300px;
}

.task-selector select.select-empty {
  color: #999;
}

.task-stats {
  color: #666;
  font-size: 14px;
}

.task-actions {
  display: flex;
  gap: 8px;
}

/* 按钮样式 */
.btn {
  padding: 8px 16px;
  border: 1px solid transparent;
  border-radius: 5px;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.2s ease;
}

.btn:disabled {
  background-color: #eef0f6;
  color: #c0c4cc;
  border-color: #dcdfe6;
  opacity: 0.7;
  cursor: not-allowed;
}
.btn-primary { background-color: #2e3968; color: #fff; }
.btn-primary:hover:not(:disabled) { background-color: white; color: #2e3968; border-color: #2e3968; }
.btn-secondary { background-color: #f0f0f0; color: #333; border-color: #ccc; }
.btn-secondary:hover:not(:disabled) { background-color: #2e3968; color: white; border-color: #2e3968; }
.btn-danger { background-color: #dc3545; color: #fff; }
.btn-danger:hover:not(:disabled) { background-color: white; color: #dc3545; border-color: #dc3545; }
.btn-warning { background-color: #ffc107; color: #000; }
.btn-warning:hover:not(:disabled) { background-color: white; color: #e6a800; border-color: #ffc107; }
.btn-sm { padding: 4px 12px; font-size: 13px; }
.btn-xs { padding: 2px 8px; font-size: 12px; }

/* 进度条 */
.task-progress {
  background: #f8f9fa;
  padding: 16px;
  border-radius: 8px;
  margin-top: 16px;
  border: 2px solid #e0e0e0;
}

.task-progress.crawling-progress {
  background: linear-gradient(135deg, #e3f2fd 0%, #f8f9fa 100%);
  border-color: #2196f3;
}

.task-progress.parsing-progress {
  background: linear-gradient(135deg, #f3e5f5 0%, #f8f9fa 100%);
  border-color: #9c27b0;
}

.progress-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.status-badge {
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 13px;
  font-weight: 600;
}

.status-badge.crawling {
  background: #e3f2fd;
  color: #1565c0;
}

.status-badge.parsing {
  background: #f3e5f5;
  color: #7b1fa2;
}

.auto-refresh {
  font-size: 12px;
  color: #666;
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
}

.progress-step { margin-bottom: 16px; }
.progress-step:last-of-type { margin-bottom: 12px; }
.progress-step.completed { opacity: 0.7; }

.progress-label {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 6px;
  font-size: 14px;
}

.step-icon { font-size: 16px; }
.progress-numbers { margin-left: auto; color: #666; font-weight: 500; }

.progress-bar {
  height: 8px;
  background: #e0e0e0;
  border-radius: 4px;
  overflow: hidden;
}

.progress-bar.crawl-bar {
  height: 16px;
  border-radius: 8px;
}

.progress-fill {
  height: 100%;
  transition: width 0.5s ease;
}

.progress-fill.crawling { background-color: #2980b9; }
.progress-fill.parsing { background-color: #8e44ad; }
.progress-fill.completed { background-color: #27ae60; }

.progress-fill.animated {
  animation: progress-stripes 1s linear infinite;
  background-size: 30px 30px;
}

.progress-fill.crawling.animated {
  background-image: linear-gradient(
    45deg,
    rgba(255,255,255,0.2) 25%,
    transparent 25%,
    transparent 50%,
    rgba(255,255,255,0.2) 50%,
    rgba(255,255,255,0.2) 75%,
    transparent 75%,
    transparent
  );
}

.progress-fill.parsing.animated {
  background-image: linear-gradient(
    45deg,
    rgba(255,255,255,0.2) 25%,
    transparent 25%,
    transparent 50%,
    rgba(255,255,255,0.2) 50%,
    rgba(255,255,255,0.2) 75%,
    transparent 75%,
    transparent
  );
}

@keyframes progress-stripes {
  0% { background-position: 30px 0; }
  100% { background-position: 0 0; }
}

.progress-percent {
  color: #888;
  font-size: 12px;
  margin-left: 4px;
}

/* 任务状态区域 */
.task-failed-section, .links-collected-section {
  padding: 16px;
  border-radius: 8px;
  margin-top: 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
}

.task-failed-section {
  background: #fff3cd;
  border: 2px solid #ffc107;
}

.links-collected-section {
  background: #d4edda;
  border: 2px solid #28a745;
}

.failed-info h4, .collected-info h4 {
  margin: 0 0 8px 0;
  font-size: 16px;
}

.failed-info h4 { color: #856404; }
.collected-info h4 { color: #155724; }

.failed-info p, .collected-info p {
  margin: 4px 0;
  font-size: 14px;
}

.parse-stats {
  background: rgba(255, 255, 255, 0.6);
  padding: 8px 12px;
  border-radius: 4px;
  margin: 8px 0;
}

.parse-stats p {
  margin: 0;
  font-size: 14px;
}

.failed-stat {
  color: #dc3545;
  font-weight: 500;
}

.action-buttons {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

.hint { color: #666; font-size: 13px; }

.year-select {
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  min-width: 120px;
}

.crawl-date-group label {
  display: block;
  margin-bottom: 6px;
  font-weight: 500;
}

.crawl-date-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}

.crawl-date-input {
  padding: 8px 10px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  min-width: 140px;
}

.crawl-date-sep {
  color: #666;
  font-size: 14px;
}

/* 对话框 */
.modal {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0,0,0,0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-content {
  background: #fff;
  padding: 24px;
  border-radius: 12px;
  max-width: 500px;
  width: 90%;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
}

.modal-content.modal-large {
  max-width: 800px;
  max-height: 80vh;
  overflow-y: auto;
}

.modal-content h3 {
  margin: 0 0 20px;
  color: #2e3968;
  font-size: 18px;
  font-weight: 700;
}

.form-group { margin-bottom: 16px; }
.form-group label {
  display: block;
  margin-bottom: 4px;
  font-weight: 600;
  color: #2e3968;
  font-size: 13px;
}
.form-group input, .form-group select {
  padding: 8px 12px;
  border: 1px solid #d0d5dd;
  border-radius: 6px;
  font-size: 13px;
  color: #2e3968;
  transition: border-color 0.2s, box-shadow 0.2s;
}

.form-group input:focus, .form-group select:focus {
  outline: none;
  border-color: #2e3968;
  box-shadow: 0 0 0 3px rgba(46, 57, 104, 0.1);
}

.radio-group { display: flex; gap: 16px; flex-wrap: wrap; }
.radio-group label { display: flex; align-items: center; gap: 4px; cursor: pointer; }

.estimate-info {
  background: #e8f4ff;
  padding: 12px;
  border-radius: 4px;
  margin-bottom: 16px;
}

.estimate-info p { margin: 4px 0; font-size: 13px; }

.modal-actions { display: flex; gap: 8px; justify-content: flex-end; margin-top: 20px; }

/* 任务表格 */
.task-table {
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 16px;
}

.task-table th, .task-table td {
  padding: 10px 8px;
  border: 1px solid #e0e0e0;
  text-align: left;
}

.task-table th { background: #f5f5f5; }
.th-checkbox, .td-checkbox { width: 36px; text-align: center; }
.row-selected { background: #fff8e1; }

.status-pending { color: #6c757d; }
.status-crawling, .status-parsing, .status-parse_queued { color: #007bff; }
.status-completed { color: #28a745; }
.status-failed { color: #dc3545; }
.status-cancelled { color: #ffc107; }

/* 清理列表 */
.cleanup-list {
  max-height: 300px;
  overflow-y: auto;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  margin-bottom: 16px;
}

.cleanup-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  border-bottom: 1px solid #e0e0e0;
  cursor: pointer;
}

.cleanup-item:hover { background: #f8f9fa; }
.cleanup-item:last-child { border-bottom: none; }

.item-stats { margin-left: auto; color: #666; font-size: 13px; }

.sync-status { font-size: 12px; padding: 2px 8px; border-radius: 4px; }
.sync-yes { background: #d4edda; color: #155724; }
.sync-no { background: #f8f9fa; color: #6c757d; }

.warning { color: #856404; font-size: 13px; }
.stats-info { color: #666; margin-bottom: 12px; }

/* PDF解析任务框样式 */
.pdf-parse-panel {
  background: linear-gradient(135deg, #f8f9ff 0%, #fff 100%);
  border: 2px solid #667eea;
  border-radius: 12px;
  padding: 20px;
  margin-top: 16px;
}

.pdf-parse-panel .panel-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 1px solid #e0e0e0;
}

.pdf-parse-panel .panel-header h4 {
  margin: 0;
  font-size: 16px;
  color: #2e3968;
}

.parse-status-badge {
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
}

.parse-status-badge.status-completed {
  background: #d4edda;
  color: #155724;
}

.parse-status-badge.status-parsing {
  background: #d1ecf1;
  color: #0c5460;
}

.parse-status-badge.status-failed {
  background: #f8d7da;
  color: #721c24;
}

.parse-status-badge.status-partial {
  background: #fff3cd;
  color: #856404;
}

.parse-status-badge.status-interrupted {
  background: #fff3cd;
  color: #856404;
}

.parse-status-badge.status-pending {
  background: #e2e3e5;
  color: #383d41;
}

.parse-stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(100px, 1fr));
  gap: 12px;
  margin-bottom: 16px;
}

.stat-item {
  background: #fff;
  padding: 12px;
  border-radius: 8px;
  text-align: center;
  border: 1px solid #e0e0e0;
}

.stat-item.success { border-color: #28a745; background: #f8fff8; }
.stat-item.pending { border-color: #ffc107; background: #fffdf8; }
.stat-item.failed { border-color: #dc3545; background: #fff8f8; }

.stat-item .stat-label {
  display: block;
  font-size: 12px;
  color: #666;
  margin-bottom: 4px;
}

.stat-item .stat-value {
  display: block;
  font-size: 20px;
  font-weight: 700;
  color: #2e3968;
}

.stat-item.success .stat-value { color: #28a745; }
.stat-item.pending .stat-value { color: #856404; }
.stat-item.failed .stat-value { color: #dc3545; }

.parse-progress-section {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
}

.progress-bar.large {
  flex: 1;
  height: 12px;
}

.progress-text {
  font-size: 14px;
  font-weight: 600;
  color: #2e3968;
  min-width: 80px;
  text-align: right;
}

.parse-actions {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.all-done-text {
  color: #155724;
  font-weight: 600;
  font-size: 14px;
}

.error-info {
  margin-top: 12px;
  padding: 10px 14px;
  background: #fff3cd;
  border-radius: 6px;
  display: flex;
  align-items: flex-start;
  gap: 8px;
}

.error-info .error-icon {
  font-size: 16px;
}

.error-info .error-text {
  font-size: 13px;
  color: #856404;
  word-break: break-word;
}

/* 链接收集完成提示 */
.links-collected-hint {
  margin-top: 12px;
  padding: 10px 14px;
  background: #e8f4ff;
  border-radius: 6px;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: #1565c0;
}

.links-collected-hint .hint-icon {
  font-size: 16px;
}

/* 完成图标样式 */
.completed-icon {
  display: inline-block;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: #28a745;
  position: relative;
}
.completed-icon::after {
  content: '';
  position: absolute;
  left: 6px;
  top: 3px;
  width: 5px;
  height: 10px;
  border: solid white;
  border-width: 0 2px 2px 0;
  transform: rotate(45deg);
}

/* Toast 提示 */
.toast {
  position: fixed;
  top: 20px;
  left: 50%;
  transform: translateX(-50%);
  padding: 10px 24px;
  border-radius: 6px;
  font-size: 14px;
  z-index: 9999;
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(0,0,0,0.15);
  max-width: 500px;
}
.toast-success { background: #d4edda; color: #155724; border: 1px solid #c3e6cb; }
.toast-error { background: #f8d7da; color: #721c24; border: 1px solid #f5c6cb; }
.toast-warning { background: #fff3cd; color: #856404; border: 1px solid #ffeeba; }

.toast-fade-enter-active, .toast-fade-leave-active {
  transition: opacity 0.3s, transform 0.3s;
}
.toast-fade-enter, .toast-fade-leave-to {
  opacity: 0;
  transform: translateX(-50%) translateY(-12px);
}

/* 模态框动画 */
.modal-fade-enter-active { transition: opacity 0.25s ease; }
.modal-fade-leave-active { transition: opacity 0.2s ease; }
.modal-fade-enter, .modal-fade-leave-to { opacity: 0; }
.modal-fade-enter-active .modal-content { animation: modal-scale-in 0.25s ease; }
.modal-fade-leave-active .modal-content { animation: modal-scale-out 0.2s ease; }
@keyframes modal-scale-in {
  from { opacity: 0; transform: scale(0.95) translateY(-10px); }
  to { opacity: 1; transform: scale(1) translateY(0); }
}
@keyframes modal-scale-out {
  from { opacity: 1; transform: scale(1) translateY(0); }
  to { opacity: 0; transform: scale(0.95) translateY(10px); }
}
</style>

